import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Icons from "@fortawesome/free-solid-svg-icons";
import { PickupRequestDetail } from "../../../../app/data/pickup-requests/models";
import {
  formatPickupReviewAuditDateTime,
  getPickupRequestReviewAuditHistory,
  resolvePickupRequestReviewAudit,
} from "../utils/pickupRequestReviewAuditUtils";

export interface PickupRequestReviewAuditProps {
  pickupRequest: PickupRequestDetail;
  pickupNumber?: string;
}

const PickupRequestReviewAudit: React.FC<PickupRequestReviewAuditProps> = ({
  pickupRequest,
  pickupNumber,
}) => {
  const audit = resolvePickupRequestReviewAudit(pickupRequest);
  if (!audit) return null;

  const historyItems = getPickupRequestReviewAuditHistory(pickupRequest, audit);

  const bannerClass = audit.isApproved
    ? "xgs-pickup-review-audit__banner--accepted"
    : "xgs-pickup-review-audit__banner--rejected";

  const iconClass = audit.isApproved
    ? "xgs-pickup-review-audit__banner-icon--accepted"
    : "xgs-pickup-review-audit__banner-icon--rejected";

  const displayPickupNumber = pickupNumber ?? pickupRequest.oneXgsPickupNumber;

  return (
    <div className="xgs-pickup-review-audit">
      <div className={`xgs-pickup-review-audit__banner ${bannerClass}`}>
        <div className={`xgs-pickup-review-audit__banner-icon ${iconClass}`} aria-hidden>
          <FontAwesomeIcon
            icon={audit.isApproved ? Icons.faCheck : Icons.faTimes}
            size="sm"
          />
        </div>
        <div className="xgs-pickup-review-audit__banner-content">
          <div className="xgs-pickup-review-audit__banner-title">{audit.bannerTitle}</div>
          <div className="xgs-pickup-review-audit__banner-line2">
            <strong className="xgs-pickup-review-audit__banner-by">
              {audit.reviewedBy || "—"}
            </strong>
            {displayPickupNumber && (
              <>
                <span className="xgs-pickup-review-audit__banner-sep" aria-hidden>
                  {" "}
                  •{" "}
                </span>
                <span>Pickup {displayPickupNumber}</span>
              </>
            )}
            <span className="xgs-pickup-review-audit__banner-sep" aria-hidden>
              {" "}
              •{" "}
            </span>
            <span className="xgs-pickup-review-audit__banner-date">
              {formatPickupReviewAuditDateTime(audit.reviewedAt)}
            </span>
          </div>
          <div className="xgs-pickup-review-audit__banner-notes">
            <strong>{audit.notesLabel} : </strong>
            <span className="xgs-pickup-review-audit__banner-notes-text">
              {audit.notes || "—"}
            </span>
          </div>
        </div>
      </div>

      <div className="xgs-pickup-review-audit__history">
        <h3 className="xgs-pickup-review-audit__history-heading">Audit History</h3>
        <div className="xgs-pickup-review-audit__timeline">
          {historyItems.map((item, index) => (
            <div key={`${item.title}-${item.at ?? index}`} className="xgs-pickup-review-audit__timeline-item">
              <span className="xgs-pickup-review-audit__timeline-dot" />
              {index < historyItems.length - 1 && (
                <span className="xgs-pickup-review-audit__timeline-line" />
              )}
              <div className="xgs-pickup-review-audit__timeline-title">{item.title}</div>
              <div className="xgs-pickup-review-audit__timeline-meta">
                {formatPickupReviewAuditDateTime(item.at)}
                {item.by ? ` · ${item.by}` : ""}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PickupRequestReviewAudit;
